// frontend/src/services/thumbnailCacheService.ts
import { getImageUrl } from '@/utils/imageUrl'
import { indexedDbService } from './indexedDbService'

// Structure pour les métadonnées stockées dans localStorage
interface CacheMetadata {
  [key: string]: {
    originalUrl: string
    timestamp: number
    size: number
  }
}

// Classe pour gérer le stockage des métadonnées dans localStorage
class MetadataStorage {
  private readonly KEY = 'thumbnail_cache_metadata'

  get(): CacheMetadata {
    try {
      const metadata = localStorage.getItem(this.KEY)
      return metadata ? JSON.parse(metadata) : {}
    } catch (error) {
      console.error('[MetadataStorage] Error reading from localStorage:', error)
      return {}
    }
  }

  set(metadata: CacheMetadata): void {
    try {
      localStorage.setItem(this.KEY, JSON.stringify(metadata))
    } catch (error) {
      console.error('[MetadataStorage] Error writing to localStorage:', error)
    }
  }

  getEntry(key: string) {
    return this.get()[key]
  }

  setEntry(key: string, entry: CacheMetadata[string]): void {
    const all = this.get()
    all[key] = entry
    this.set(all)
  }

  removeEntry(key: string): void {
    const all = this.get()
    delete all[key]
    this.set(all)
  }

  clear(): void {
    localStorage.removeItem(this.KEY)
  }
}

class ThumbnailCacheService {
  // Cache en mémoire pour les object URLs, afin d'éviter de les recréer
  private memoryCache = new Map<string, string>()
  private metadataStorage = new MetadataStorage()
  // Suivi des requêtes en cours pour pouvoir les annuler
  private pendingRequests = new Map<string, AbortController>()

  private readonly MAX_CACHE_AGE = 7 * 24 * 60 * 60 * 1000 // 7 jours
  private readonly MAX_CACHE_BYTES = 50 * 1024 * 1024 // 50MB

  private getCacheKey(imageUrl: string): string {
    return imageUrl.split('/').pop()?.split('?')[0] || imageUrl
  }

  async getThumbnail(imageUrl: string, abortSignal?: AbortSignal): Promise<string | null> {
    if (!imageUrl) return null

    // Normaliser l'URL : transformer les chemins relatifs en URLs complètes
    const normalizedUrl = getImageUrl(imageUrl) || imageUrl
    const cacheKey = this.getCacheKey(normalizedUrl)

    // 1. Vérifier le cache mémoire (le plus rapide)
    if (this.memoryCache.has(cacheKey)) {
      //console.log(`[ThumbnailCache] ✅ CACHE HIT (Memory - Session): ${cacheKey}`)
      return this.memoryCache.get(cacheKey)!
    }

    // 2. Vérifier le cache persistant (IndexedDB)
    const metadata = this.metadataStorage.getEntry(cacheKey)
    if (metadata && Date.now() - metadata.timestamp < this.MAX_CACHE_AGE) {
      const blob = await indexedDbService.getThumbnail(cacheKey)
      if (blob) {
        //console.log(`[ThumbnailCache] ✅ CACHE HIT (Persistent - IndexedDB): ${cacheKey}`)
        const objectUrl = URL.createObjectURL(blob)
        this.memoryCache.set(cacheKey, objectUrl)
        return objectUrl
      } else {
        console.warn(
          `[ThumbnailCache] 🟡 Metadata found for ${cacheKey} but no blob in DB. Cleaning up.`
        )
        this.metadataStorage.removeEntry(cacheKey)
      }
    }

    // 3. Cache MISS, télécharger depuis le réseau avec retry
    //console.log(
    //  `[ThumbnailCache] 🌐 CACHE MISS (Network fetch): ${cacheKey} from URL ${normalizedUrl}`
    //)
    return this.fetchWithRetry(normalizedUrl, cacheKey, abortSignal)
  }

  private async fetchWithRetry(
    imageUrl: string,
    cacheKey: string,
    externalAbortSignal?: AbortSignal,
    maxRetries: number = 1,
    retryDelay: number = 1000
  ): Promise<string | null> {
    // Annuler toute requête en cours pour cette clé
    this.cancelRequest(cacheKey)

    // Créer un AbortController pour cette requête
    const controller = new AbortController()
    this.pendingRequests.set(cacheKey, controller)

    // Combiner avec le signal externe si fourni
    const combinedSignal = externalAbortSignal
      ? this.combineAbortSignals([controller.signal, externalAbortSignal])
      : controller.signal

    // L'URL devrait déjà être normalisée, mais on s'assure qu'elle est complète
    let correctedUrl = getImageUrl(imageUrl) || imageUrl

    // Correction pour les anciennes URLs mock
    if (correctedUrl.includes('/mock-data/graffitis/BDC_2017_thumbnails/')) {
      correctedUrl = correctedUrl.replace(
        '/mock-data/graffitis/BDC_2017_thumbnails/',
        '/images/graffitis/thumbnails/'
      )
      // Re-normaliser après correction
      correctedUrl = getImageUrl(correctedUrl) || correctedUrl
      console.log(`[ThumbnailCache] 🔧 URL corrected to: ${correctedUrl}`)
    }

    // Log pour debug
    if (!correctedUrl.startsWith('http://') && !correctedUrl.startsWith('https://')) {
      console.warn(
        `[ThumbnailCache] ⚠️ URL non absolue détectée: ${correctedUrl}, transformation...`
      )
      correctedUrl = getImageUrl(correctedUrl) || correctedUrl
    }

    let lastError: Error | null = null
    let is404Error = false

    try {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          // Vérifier si la requête a été annulée
          if (combinedSignal.aborted) {
            throw new Error('Request aborted')
          }

          if (attempt > 0) {
            const delay = retryDelay * Math.pow(2, attempt - 1) // Délai exponentiel
            console.log(
              `[ThumbnailCache] 🔄 Retry ${attempt + 1}/${maxRetries} pour ${cacheKey} après ${delay}ms`
            )
            await new Promise(resolve => setTimeout(resolve, delay))
          }

          // Vérifier à nouveau si annulé après le délai
          if (combinedSignal.aborted) {
            throw new Error('Request aborted')
          }

          const response = await fetch(correctedUrl, {
            cache: 'no-cache', // Forcer le re-téléchargement
            signal: combinedSignal,
          })
          if (!response.ok) {
            // Ne pas retry pour les erreurs 404 (fichier n'existe pas)
            if (response.status === 404) {
              is404Error = true
              console.warn(
                `[ThumbnailCache] ⚠️ Image non trouvée (404) pour ${cacheKey}, pas de retry`
              )
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          const blob = await response.blob()

          // Vérifier que c'est bien une image valide
          if (blob.size === 0 || !blob.type.startsWith('image/')) {
            throw new Error(`Invalid image blob (size: ${blob.size}, type: ${blob.type})`)
          }

          // Stocker dans le cache persistant
          await indexedDbService.saveThumbnail(cacheKey, blob)
          this.metadataStorage.setEntry(cacheKey, {
            originalUrl: correctedUrl, // Stocker l'URL normalisée
            timestamp: Date.now(),
            size: blob.size,
          })

          // Nettoyer le cache si nécessaire
          this.cleanup()

          const objectUrl = URL.createObjectURL(blob)
          this.memoryCache.set(cacheKey, objectUrl)
          this.pendingRequests.delete(cacheKey)
          //console.log(`[ThumbnailCache] ✅ Image téléchargée et mise en cache: ${cacheKey}`)
          return objectUrl
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error))

          // Si la requête a été annulée, ne pas retry
          if (lastError.name === 'AbortError' || combinedSignal.aborted) {
            console.log(`[ThumbnailCache] ⏹️ Requête annulée pour ${cacheKey}`)
            this.pendingRequests.delete(cacheKey)
            throw lastError
          }

          // Si erreur 404, arrêter immédiatement les retries
          if (is404Error) {
            console.warn(
              `[ThumbnailCache] ⚠️ Image non trouvée (404) pour ${cacheKey}, arrêt des retries`
            )
            break
          }
          console.warn(
            `[ThumbnailCache] ⚠️ Tentative ${attempt + 1}/${maxRetries} échouée pour ${cacheKey}:`,
            lastError.message
          )
        }
      }

      console.error(
        `[ThumbnailCache] ❌ Échec après ${maxRetries} tentatives pour ${correctedUrl}:`,
        lastError
      )
      this.pendingRequests.delete(cacheKey)
      // Ne pas retourner l'URL originale en cas d'échec, retourner null pour forcer le retry côté composant
      return null
    } catch (error) {
      this.pendingRequests.delete(cacheKey)
      // Si c'est une erreur d'annulation, la propager
      if (error instanceof Error && (error.name === 'AbortError' || combinedSignal.aborted)) {
        throw error
      }
      throw error
    }
  }

  private combineAbortSignals(signals: AbortSignal[]): AbortSignal {
    // Si un signal est déjà annulé, retourner un signal annulé
    for (const signal of signals) {
      if (signal.aborted) {
        return signal
      }
    }

    const controller = new AbortController()

    const abort = () => {
      if (!controller.signal.aborted) {
        controller.abort()
      }
    }

    signals.forEach(signal => {
      signal.addEventListener('abort', abort, { once: true })
    })

    return controller.signal
  }

  cancelRequest(cacheKey: string): void {
    const controller = this.pendingRequests.get(cacheKey)
    if (controller) {
      controller.abort()
      this.pendingRequests.delete(cacheKey)
    }
  }

  cancelAllRequests(): void {
    for (const [_cacheKey, controller] of this.pendingRequests.entries()) {
      controller.abort()
    }
    this.pendingRequests.clear()
  }

  async cleanup(): Promise<void> {
    const metadata = this.metadataStorage.get()
    const entries = Object.entries(metadata)
    let totalSize = entries.reduce((acc, [, meta]) => acc + meta.size, 0)

    if (totalSize <= this.MAX_CACHE_BYTES) {
      // console.log('[ThumbnailCache] Cleanup not needed. Cache size is within limits.')
      return
    }

    // console.log(`[ThumbnailCache] Cleanup needed. Current size: ${(totalSize / 1024 / 1024).toFixed(2)}MB`)

    // Trier par date (le plus ancien en premier)
    entries.sort(([, a], [, b]) => a.timestamp - b.timestamp)

    for (const [key, meta] of entries) {
      if (totalSize <= this.MAX_CACHE_BYTES) break

      await indexedDbService.deleteThumbnail(key)
      this.metadataStorage.removeEntry(key)
      if (this.memoryCache.has(key)) {
        URL.revokeObjectURL(this.memoryCache.get(key)!)
        this.memoryCache.delete(key)
      }

      totalSize -= meta.size
      // console.log(`[ThumbnailCache] Evicted ${key} from cache. New size: ${(totalSize / 1024 / 1024).toFixed(2)}MB`)
    }
  }

  async getStats() {
    const metadata = this.metadataStorage.get()
    const entries = Object.entries(metadata)
    const totalSize = entries.reduce((acc, [, meta]) => acc + meta.size, 0)

    return {
      totalEntries: entries.length,
      totalSizeKB: Math.round(totalSize / 1024),
      totalSizeMB: Math.round((totalSize / (1024 * 1024)) * 100) / 100,
      maxSizeMB: this.MAX_CACHE_BYTES / (1024 * 1024),
      usage: `${Math.round((totalSize / this.MAX_CACHE_BYTES) * 100)}%`,
    }
  }

  async preloadThumbnails(imageUrls: string[]): Promise<void> {
    // console.log(`[ThumbnailCache] Preloading ${imageUrls.length} thumbnails...`)
    const promises = imageUrls.map(url =>
      this.getThumbnail(url).catch(error => {
        console.warn(`[ThumbnailCache] Failed to preload ${url}:`, error)
      })
    )
    await Promise.allSettled(promises)
    // console.log(`[ThumbnailCache] Preloading finished.`)
  }

  async invalidateEntry(imageUrl: string): Promise<void> {
    // Normaliser l'URL avant d'invalider
    const normalizedUrl = getImageUrl(imageUrl) || imageUrl
    const cacheKey = this.getCacheKey(normalizedUrl)
    // Supprimer de IndexedDB
    await indexedDbService.deleteThumbnail(cacheKey)
    // Supprimer des métadonnées
    this.metadataStorage.removeEntry(cacheKey)
    // Supprimer du cache mémoire
    if (this.memoryCache.has(cacheKey)) {
      URL.revokeObjectURL(this.memoryCache.get(cacheKey)!)
      this.memoryCache.delete(cacheKey)
    }
    console.log(`[ThumbnailCache] Entry invalidated: ${cacheKey}`)
  }

  async clear(): Promise<void> {
    await indexedDbService.clearThumbnails()
    this.metadataStorage.clear()
    for (const url of this.memoryCache.values()) {
      URL.revokeObjectURL(url)
    }
    this.memoryCache.clear()
    console.log('[ThumbnailCache] All caches cleared.')
  }

  revokeMemoryUrls(): void {
    for (const url of this.memoryCache.values()) {
      URL.revokeObjectURL(url)
    }
    this.memoryCache.clear()
    // console.log('[ThumbnailCache] Revoked all in-memory object URLs.')
  }
}

export const thumbnailCache = new ThumbnailCacheService()

// Nettoyage périodique
setInterval(
  () => {
    // console.log('[ThumbnailCache] Running periodic cleanup...')
    thumbnailCache.cleanup()
  },
  10 * 60 * 1000
) // Toutes les 10 minutes

// Lorsque l'utilisateur quitte la page, on peut vider le cache mémoire
// window.addEventListener('beforeunload', () => {
//   thumbnailCache.revokeMemoryUrls()
// })
